import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import Breadcrumbs from '../../components/ui/breadcrumbs';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import { Building, Trash2, Search, Users, Globe, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axios';

const AdminOrganizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    // Filter organizations based on search term
    const filtered = organizations.filter(org =>
      org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.website?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.owner?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOrganizations(filtered);
  }, [organizations, searchTerm]);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get('/admin/organizations');

      // Handle different response formats
      let organizationsData = [];
      if (response.data?.organizations?.data) {
        // Paginated response
        organizationsData = response.data.organizations.data;
      } else if (response.data?.organizations) {
        // Direct array response
        organizationsData = response.data.organizations;
      } else if (response.data?.data) {
        // Alternative structure
        organizationsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Direct array
        organizationsData = response.data;
      }

      setOrganizations(Array.isArray(organizationsData) ? organizationsData : []);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      toast.error('Failed to fetch organizations');
      setOrganizations([]); // Ensure organizations is always an array
    } finally {
      setLoading(false);
    }
  };

  const deleteOrganization = async (id) => {
    if (window.confirm('Are you sure youwant to delete this organization? This will also remove all associated users.')) {
      try {
        await axios.delete(`/admin/organizations/${id}`);
        toast.success('Organization deleted successfully');
        fetchOrganizations();
      } catch (error) {
        toast.error('Failed to delete organization');
      }
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6"><Breadcrumbs /></div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Organization Management</h1>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Organizations ({filteredOrganizations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredOrganizations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? 'No organizations found matching your search' : 'No organizations found'}
              </div>
            ) : (
              filteredOrganizations.map((org) => (
                <div key={org.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Building className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium text-lg">{org.name}</div>
                      <div className="text-sm text-gray-500 space-y-1">
                        {org.website && (
                          <div className="flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            <a
                              href={org.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {org.website}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          Owner: {org.owner?.name || 'Unknown'}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Created {new Date(org.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="text-sm font-medium">
                        {org.users_count || 0} members
                      </div>
                      <div className="text-xs text-gray-500">
                        {org.llm_requests_count || 0} LLM requests
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteOrganization(org.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOrganizations;
